import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import {
  trackCTA,
  trackInvitationPreviewViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { buildDemoInvitationUrl } from "../../lib/demoInvitation.js";

/**
 * "An RSVP you can answer right now."
 *
 * The one framed, centred block on a page of left-aligned editorial sections —
 * the same `dwi-demo` treatment /digital-wedding-invitation and
 * /wedding-planning use, and for the same reason: on a page of statements, the
 * one thing that is an OFFER should not look like another paragraph. Reusing
 * those classes rather than minting a fourth identical block is also why this
 * page carries `dwi-page`.
 *
 * The offer is honest because the demo RSVP is real: `demo-heirloom` is
 * reserved on the backend, `GET /rsvp-sections` returns a hard-coded ceremony +
 * reception pair (`buildDemoRsvpSections`), and the submit short-circuits with
 * `demo: true` before turnstile, before the rate limiter and before any write
 * (api/routes/rsvp.routes.mjs). So "nothing you enter is saved" is literally
 * what the route does, not a reassurance.
 *
 * The impression reuses the EXISTING `invitation_preview` section name via
 * `trackInvitationPreviewViewed` — the same one the homepage pricing card and
 * the invitation page already fire for this exact URL — so the three surfaces
 * are directly comparable instead of splitting one funnel across three names.
 * `trackLead` does not fire: opening the sample provisions nothing.
 */
const RsvpDemo = () => {
  const { t } = useTranslation();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackInvitationPreviewViewed(analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="dwi-demo">
          <p className="wps-eyebrow">{t("weddingRsvp.demo.eyebrow")}</p>
          <h2 className="heading-50">{t("weddingRsvp.demo.heading")}</h2>
          <p className="body-20 dwi-demo-body">{t("weddingRsvp.demo.body")}</p>
          <div className="dwi-demo-action">
            <Button
              href={buildDemoInvitationUrl(shortLanguage)}
              target="_blank"
              text={t("weddingRsvp.demo.cta")}
              className="button-lg"
              onClick={() =>
                trackCTA("wedding_rsvp_demo", {
                  ...analyticsContext,
                  ctaName: "view_demo",
                })
              }
            />
          </div>
          <p className="wps-note dwi-demo-note">{t("weddingRsvp.demo.note")}</p>
        </div>
      </div>
    </section>
  );
};

export default RsvpDemo;
